import { z } from 'zod';
import { isIsoDay } from '../utils/checklist.js';
import { EXPENSE_LIST_SORTS, ExpensePhase } from '../types/expense.js';
import { EXPENSE_CASH_FILTERS } from '../types/installment.js';
/** The largest amount a `Decimal(12, 2)` column holds. */
export const MAX_MONEY_AMOUNT = 9_999_999_999.99;
/**
 * An amount of money as the database stores it: finite, within
 * `Decimal(12, 2)`, and in whole cents. A larger value used to reach Postgres
 * and come back as a 500, and a third decimal was rounded away silently.
 */
export const moneyAmountSchema = z
    .number()
    .finite()
    .min(0)
    .max(MAX_MONEY_AMOUNT)
    .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, 'At most two decimal places');
/**
 * A date-only field: `YYYY-MM-DD` (a real day), or a full ISO timestamp from
 * an older caller. Anything else used to reach `new Date()` and fail as a 500.
 */
export const dateInputSchema = z.string().refine(
// A timestamp's day must be a real one too: Date.parse rolls 2026-02-30 on to March 2.
(v) => isIsoDay(v) ||
    (/^\d{4}-\d{2}-\d{2}T/.test(v) && isIsoDay(v.slice(0, 10)) && !Number.isNaN(Date.parse(v))), 'Not a valid date');
export const createExpenseItemSchema = z.object({
    eventId: z.string().uuid(),
    vendorCategoryId: z.string().uuid(),
    catalogItemId: z.string().uuid().optional(),
    itemName: z.string().max(255).optional(),
    vendorId: z.string().uuid().optional(),
    vendorName: z.string().max(255).optional(),
    // Optional — an expense line can be created without an amount and priced later.
    price: moneyAmountSchema.optional(),
    /** Omitted: booked when a vendor or price is given, else a need being sourced. */
    phase: z.nativeEnum(ExpensePhase).optional(),
    budgetAmount: moneyAmountSchema.nullable().optional(),
    statusId: z.string().uuid().optional(),
    dueDate: dateInputSchema.optional(),
    comments: z.string().max(2000).optional(),
});
const CLIENT_APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const updateExpenseItemSchema = z.object({
    vendorCategoryId: z.string().uuid().optional(),
    catalogItemId: z.string().uuid().nullable().optional(),
    itemName: z.string().max(255).nullable().optional(),
    vendorId: z.string().uuid().nullable().optional(),
    vendorName: z.string().max(255).nullable().optional(),
    price: moneyAmountSchema.optional(),
    budgetAmount: moneyAmountSchema.nullable().optional(),
    statusId: z.string().uuid().optional(),
    dueDate: dateInputSchema.nullable().optional(),
    comments: z.string().max(2000).nullable().optional(),
    sortOrder: z.number().int().optional(),
    clientApproval: z.enum(CLIENT_APPROVAL_STATUSES).optional(),
});
export const createExpensePaymentSchema = z.object({
    amount: moneyAmountSchema.refine((v) => v > 0, 'A payment must be more than 0'),
    paymentMethodId: z.string().uuid(),
    paidAt: dateInputSchema.optional(),
    notes: z.string().max(2000).optional(),
});
/**
 * "Mark paid in full": the remaining balance, worked out by the server under
 * the line's lock, recorded as one payment.
 */
export const markPaidInFullSchema = z.object({
    paymentMethodId: z.string().uuid(),
    paidAt: dateInputSchema.optional(),
    notes: z.string().max(2000).optional(),
});
/** One installment of a schedule. */
export const installmentInputSchema = z.object({
    label: z.string().trim().max(100).nullable().optional(),
    amount: moneyAmountSchema.refine((v) => v > 0, 'An installment must be more than 0'),
    dueDate: z.string().refine(isIsoDay, 'Not a valid date (YYYY-MM-DD)'),
});
/**
 * A line's whole schedule, replaced at once. Empty clears it (the line falls
 * back to its single due date); otherwise the sum must equal the price, which
 * the server checks against the price it holds.
 */
export const replaceInstallmentsSchema = z.object({
    installments: z.array(installmentInputSchema).max(60),
});
/** What a new item line of a catalog item starts at, and is counted in. */
const catalogDefaults = {
    defaultUnitPrice: moneyAmountSchema.nullable().optional(),
    unit: z
        .string()
        .trim()
        .max(64)
        .nullable()
        .optional()
        .transform((v) => (v === '' ? null : v)),
};
export const createCatalogItemSchema = z.object({
    vendorCategoryId: z.string().uuid(),
    name: z.string().min(1).max(255),
    nameHe: z.string().max(255).optional(),
    sortOrder: z.number().int().optional(),
    ...catalogDefaults,
});
export const updateCatalogItemSchema = z.object({
    vendorCategoryId: z.string().uuid().optional(),
    name: z.string().min(1).max(255).optional(),
    nameHe: z.string().max(255).nullable().optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
    ...catalogDefaults,
});
export const deleteCatalogItemSchema = z.object({
    reassignToCatalogItemId: z.string().uuid().nullable().optional(),
});
// ─── Client Payments ────────────────────────────────────────────────────────
export const clientPaymentAllocationInputSchema = z.object({
    expenseItemId: z.string().uuid(),
    amount: z.number().min(0),
});
export const createClientPaymentSchema = z.object({
    eventId: z.string().uuid(),
    amount: z.number().min(0),
    paymentMethodId: z.string().uuid(),
    paidAt: z.string().optional(),
    reference: z.string().max(255).optional(),
    notes: z.string().max(2000).optional(),
    allocations: z.array(clientPaymentAllocationInputSchema).optional(),
    closeExpenses: z.boolean().optional(),
});
export const updateClientPaymentSchema = z.object({
    amount: z.number().min(0).optional(),
    paymentMethodId: z.string().uuid().optional(),
    paidAt: z.string().optional(),
    reference: z.string().max(255).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    allocations: z.array(clientPaymentAllocationInputSchema).optional(),
    closeExpenses: z.boolean().optional(),
});
/** A comma-separated list of ids in a query string (`a,b,c`); empty entries dropped. */
const idListParam = z
    .string()
    .transform((v) => v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean))
    .pipe(z.array(z.string().uuid()).max(200));
/**
 * `GET /expenses` as a page: filters, sort and paging, all applied by the
 * server so the rows, the total and the summary describe one set.
 */
export const expenseListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    eventId: z.string().uuid().optional(),
    eventIds: idListParam.optional(),
    vendorId: z.string().uuid().optional(),
    categoryIds: idListParam.optional(),
    statusIds: idListParam.optional(),
    q: z.string().trim().max(200).optional(),
    sort: z.enum(EXPENSE_LIST_SORTS).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
    /** Booked lines only, or needs only; absent is both. */
    phase: z.nativeEnum(ExpensePhase).optional(),
    /** Quick cash filters, any of which matches (`overdue,due30`). */
    cash: z
        .string()
        .transform((v) => v
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean))
        .pipe(z.array(z.enum(EXPENSE_CASH_FILTERS)).max(EXPENSE_CASH_FILTERS.length))
        .optional(),
});
//# sourceMappingURL=expense.js.map